import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import { S3Serve } from '../../services/api/S3Serv';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';



function S3Explorer() {
  const [objects, setObjects] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showFoldersOnly, setShowFoldersOnly] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState('knowledgeboats'); // default
  const availableBuckets = ['knowledgeboats', 'blackbox-content'];
  const toast = useRef(null);
  const fileUploadRef = useRef(null);

  useEffect(() => {
    fetchObjects();
  }, [currentPath, selectedBucket]);


  const getBreadcrumb = () => {
    if (!currentPath) return selectedBucket;

    const parts = currentPath.split('/').filter(p => p); // remove empty
    return [selectedBucket, ...parts].join(' / ');
  };

  const handleChangeBucket = (value) => {
    setSelectedBucket(value)
    setCurrentPath('')
  }

  const fetchObjects = async () => {
    setIsLoading(true);
    try {
      const response = await S3Serve.getObjects({ prefix: currentPath, bucket: selectedBucket });
      setObjects(response);
    } catch (error) {
      console.error('Error fetching objects:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch objects',
        life: 3000
      });
    }
    finally {
      setIsLoading(false);
    }
  };
  const handleDelete = (file) => {
    confirmDialog({
      message: `Are you sure you want to delete "${file.name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: async () => {
        try {
          await S3Serve.deleteObject({ key: file.name, bucket: selectedBucket });
          toast.current?.show({
            severity: 'success',
            summary: 'Deleted',
            detail: 'File deleted successfully',
            life: 3000
          });
          fetchObjects();
        } catch (err) {
          console.error('Delete failed:', err);
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete file',
            life: 3000
          });
        }
      }
    });
  };
  const handleAddFolder = async () => {
    const folderName = prompt('Enter folder name:');
    if (!folderName || folderName.trim() === '') return;

    const safeName = folderName.trim().replace(/\/+$/, ''); // Remove trailing slashes
    const folderKey = currentPath ? `${currentPath}${safeName}/` : `${safeName}/`;

    try {
      await S3Serve.createFolder({ key: folderKey, bucket: selectedBucket });
      toast.current?.show({
        severity: 'success',
        summary: 'Folder Created',
        detail: `"${safeName}" created successfully.`,
        life: 2000
      });
      fetchObjects();
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to create folder',
        life: 3000
      });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.files[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const fileName = currentPath ? `${currentPath}${file.name}` : file.name;
      const response = await S3Serve.uploadUrl({
        fileName,
        fileType: file.type,
        bucket: selectedBucket
      });

      await axios.put(response.url, file, {
        headers: {
          'Content-Type': file.type
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setUploadProgress(percentCompleted);
        }
      });

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'File uploaded successfully',
        life: 3000
      });

      fetchObjects();
      fileUploadRef.current?.clear();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to upload file',
        life: 3000
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const navigateToFolder = (folderName) => {
    setCurrentPath(folderName);
    setSearchTerm('');
  };

  const goBack = () => {
    const newPath = currentPath.split('/').slice(0, -2).join('/') + '/';
    setCurrentPath(newPath);
    setSearchTerm('');
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileName = (key) => key.replace(currentPath, '');

  const filteredObjects = [...objects]
    .filter(obj => {
      const rawName = getFileName(obj.name);
      const name = rawName.toLowerCase();
      const isFolder = obj.type === 'folder';

      if (isFolder && (obj.name == 'prod' || obj.name == 'prod/' || obj.name == 'dev' || obj.name == 'dev/')) {
        return false
      }
      // ⛔ Skip "file" objects pretending to be folders like "com/"
      if (obj.type === 'file' && obj.name.endsWith('/')) return false;

      // ✅ If toggle is ON, only allow folders
      if (showFoldersOnly && !isFolder) return false;

      // ✅ Apply search filter
      return name.includes(searchTerm.toLowerCase());
    })

  const nameBodyTemplate = (rowData) => {
    const isFolder = rowData.type === 'folder';
    return (
      <div className="flex items-center gap-2">
        <i className={`pi ${isFolder ? 'pi-folder' : 'pi-file'} text-gray-600`} />
        <span
          className={
            isFolder
              ? (getFileName(rowData.name) === 'prod' || getFileName(rowData.name) === 'prod/')
                ? 'cursor-pointer text-red-600 font-semibold hover:text-red-800'
                : 'cursor-pointer text-blue-600 hover:text-blue-800'
              : ''
          }
          onClick={() => isFolder && navigateToFolder(rowData.name)}
        >
          {getFileName(rowData.name)}
        </span>
      </div>
    );
  };

  const actionBodyTemplate = (rowData) => {
    if (rowData.type === 'file' && rowData.url) {
      const copyToClipboard = () => {
        navigator.clipboard.writeText(rowData.url)
          .then(() => {
            toast.current?.show({
              severity: 'success',
              summary: 'Copied',
              detail: 'URL copied to clipboard',
              life: 2000
            });
          })
          .catch((err) => {
            console.error('Failed to copy URL: ', err);
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to copy URL',
              life: 2000
            });
          });
      };

      return (
        <div className="flex items-center gap-2">
          <Button
            icon="pi pi-eye"
            rounded
            text
            severity="info"
            onClick={() => window.open(rowData.url, '_blank')}
            tooltip="View File"
          />
          <Button
            icon="pi pi-copy"
            rounded
            text
            severity="secondary"
            onClick={copyToClipboard}
            tooltip="Copy URL"
          />
          <Button
            icon="pi pi-trash"
            rounded
            text
            severity="danger"
            onClick={() => handleDelete(rowData)}
            tooltip="Delete"
          />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="bg-white rounded-lg shadow-md p-2">
        <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-4">
          {/* Left: Title */}
          <div className="flex align-items-center gap-2">
            
              <Button
                icon="pi pi-arrow-left"
                onClick={goBack}
                tooltip="Back"
                severity="secondary"
                className="p-button-sm"
              />
            
            <span className="text-base  font-medium leading-none ml-5">
              {getBreadcrumb()}
            </span>
          </div>

         
          {/*  Select Bucket */}
          <Dropdown value={selectedBucket} onChange={(e) => handleChangeBucket(e.value)} options={availableBuckets}
            placeholder="Select a Bucket" className="w-full md:w-14rem" />

         
        </div>
        <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-4">
           {/* Center: Search */}
           <div>
            <span className="p-input-icon-left">
              <i className="pi pi-search" style={{ marginLeft: "10px", marginRight: "10px" }} />
              <InputText
                placeholder="Search by file name..."
                value={searchTerm}
                style={{ width: "400px", padding: "10px 50px" }}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </span>
          </div>
           {/* Right: Toggle + Add Folder + Upload */}
           <div className="flex justify-content-end align-items-center gap-3">
            {/* Show Folders Only Switch */}
            <div className="flex align-items-center gap-2">
              <label htmlFor="folders-only" className="text-sm text-gray-700">Show folders only</label>
              <InputSwitch
                inputId="folders-only"
                checked={showFoldersOnly}
                onChange={(e) => setShowFoldersOnly(e.value)}
              />
            </div>

            {/* Add Folder */}
            <Button
              icon="pi pi-folder"
              label="Add Folder"
              severity="info"
              onClick={handleAddFolder}
            >
            </Button>

            {/* Upload File */}
            <FileUpload
              ref={fileUploadRef}
              mode="basic"
              name="file"
              url="/api/upload"
              accept="*/*"
              maxFileSize={50000000}
              customUpload
              uploadHandler={handleFileUpload}
              auto
              chooseLabel={isUploading ? 'Uploading...' : 'Upload File'}
              disabled={isUploading}
            />
            {/* Refresh Button */}
            <Button
              icon="pi pi-refresh"
              severity="success"
              rounded
              raised

              text
              onClick={fetchObjects}
            />
          </div>
        </div>

        {isUploading && (
          <ProgressBar
            value={uploadProgress}
            className="mb-4"
            showValue
          />
        )}

        <DataTable
          value={filteredObjects}
          tableStyle={{ minWidth: '50rem' }}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sortField="lastModified"   // ✅ Sort by date
          sortOrder={-1}
          loading={isLoading}
        >
          <Column
            field="name"
            header="Name"
            body={nameBodyTemplate}
            sortable
          />
          <Column
            field="size"
            header="Size"
            body={(rowData) => rowData?.type == 'folder' ? '-' : formatSize(rowData.size)}
            sortable
          />
          <Column
            field="lastModified"
            header="Last Modified"
            body={(rowData) => rowData.lastModified ? new Date(rowData.lastModified).toLocaleString() : '-'}
            sortable
          />
          <Column
            body={actionBodyTemplate}
            header="Actions"
            style={{ width: '8rem' }}
          />
        </DataTable>
      </div>

    </>
  );
}

export default S3Explorer;